import React, { ReactNode } from 'react';
import { Text as RNText, TextProps as RNTextProps } from 'react-native';
import { 
  Text as TamaguiText, 
  SizableText,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  TextProps as TamaguiTextProps,
  SizableTextProps,
} from 'tamagui';
import { useTranslation } from 'react-i18next';

/**
 * Props that extend base text props with i18n functionality
 */
interface I18nTextPropsBase {
  /**
   * Translation key to use instead of children
   * If provided, this will be used for translation instead of children
   */
  translationKey?: string;
  
  /**
   * Parameters to pass to the translation function for interpolation
   * Example: { name: 'John' } for "Hello {{name}}"
   */
  translationParams?: Record<string, any>;
  
  /**
   * If true, skip translation and render children as-is
   * Useful for dynamic content or already-translated text
   */
  skipTranslation?: boolean;
  
  /**
   * Children to render
   * If string and skipTranslation is false, will be translated
   */
  children?: ReactNode;
}

/**
 * Generic i18n wrapper component factory
 * Creates a wrapper that automatically translates text content
 */
function createI18nWrapper<P extends { children?: ReactNode }>(
  Component: React.ComponentType<P>
): React.FC<P & I18nTextPropsBase> {
  const WrappedComponent = React.forwardRef<any, P & I18nTextPropsBase>(
    ({ children, translationKey, translationParams, skipTranslation, ...props }, ref) => {
      const { t } = useTranslation();
      
      // Determine what to render
      let content: ReactNode = children;
      
      if (!skipTranslation) {
        // If translationKey is provided, use it
        if (translationKey) {
          content = translationParams ? t(translationKey, translationParams) : t(translationKey);
        }
        // If children is a string, translate it
        else if (typeof children === 'string') {
          content = translationParams ? t(children, translationParams) : t(children);
        }
        // If children is an array with a single string, translate it
        else if (Array.isArray(children) && children.length === 1 && typeof children[0] === 'string') {
          content = translationParams ? t(children[0], translationParams) : t(children[0]);
        }
        // Otherwise, pass children through as-is
      }
      
      return <Component {...(props as P)} ref={ref}>{content}</Component>;
    }
  );
  
  WrappedComponent.displayName = `I18n(${Component.displayName || Component.name || 'Component'})`;
  
  return WrappedComponent;
}

/**
 * I18n-enabled React Native Text component
 * 
 * Usage:
 * ```tsx
 * import { I18nRNText } from '@components/Global/I18nText';
 * 
 * // Automatic translation from children
 * <I18nRNText>Hello World</I18nRNText>
 * 
 * // Using translation key
 * <I18nRNText translationKey="welcome.message" />
 * 
 * // With translation params
 * <I18nRNText translationKey="greeting" translationParams={{ name: "John" }} />
 * 
 * // Skip translation
 * <I18nRNText skipTranslation>Already translated text</I18nRNText>
 * ```
 */
export const I18nRNText = createI18nWrapper<RNTextProps>(RNText);

/**
 * I18n-enabled Tamagui Text component
 * 
 * Usage:
 * ```tsx
 * import { I18nText } from '@components/Global/I18nText';
 * 
 * <I18nText fontSize="$4">Hello World</I18nText>
 * <I18nText translationKey="welcome.message" />
 * ```
 */
export const I18nText = createI18nWrapper<TamaguiTextProps>(TamaguiText);

/**
 * I18n-enabled Tamagui SizableText component
 * 
 * Usage:
 * ```tsx
 * import { I18nSizableText } from '@components/Global/I18nText';
 * 
 * <I18nSizableText size="$6">Hello World</I18nSizableText>
 * ```
 */
export const I18nSizableText = createI18nWrapper<SizableTextProps>(SizableText);

/**
 * I18n-enabled Tamagui H1 component
 */
export const I18nH1 = createI18nWrapper<React.ComponentProps<typeof H1>>(H1);

/**
 * I18n-enabled Tamagui H2 component
 */
export const I18nH2 = createI18nWrapper<React.ComponentProps<typeof H2>>(H2);

/**
 * I18n-enabled Tamagui H3 component
 */
export const I18nH3 = createI18nWrapper<React.ComponentProps<typeof H3>>(H3);

/**
 * I18n-enabled Tamagui H4 component
 */
export const I18nH4 = createI18nWrapper<React.ComponentProps<typeof H4>>(H4);

/**
 * I18n-enabled Tamagui H5 component
 */
export const I18nH5 = createI18nWrapper<React.ComponentProps<typeof H5>>(H5);

/**
 * I18n-enabled Tamagui H6 component
 */
export const I18nH6 = createI18nWrapper<React.ComponentProps<typeof H6>>(H6);

// Export all components
export default {
  I18nRNText,
  I18nText,
  I18nSizableText,
  I18nH1,
  I18nH2,
  I18nH3,
  I18nH4,
  I18nH5,
  I18nH6,
};

